import React, { useState, useEffect } from 'react';
import API_BASE_URL from '../config/api';
import { useAuth } from '../context/AuthContext';
import { getExchangeIcon } from '../assets/exchanges';

const Exchanges = () => {
  const [exchanges, setExchanges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userExchangeSettings, setUserExchangeSettings] = useState({});
  const [saving, setSaving] = useState(false);
  const { user } = useAuth();
  const userId = user?.id || null; // Get userId from auth context, null if not logged in

  useEffect(() => {
    document.title = 'Exchanges';
    fetchExchanges();
    // Only fetch user settings if user is logged in
    if (userId && userId !== 0) {
      fetchUserExchangeSettings();
    } else {
      setUserExchangeSettings({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const fetchExchanges = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch from API endpoint (proxy will forward to backend)
      const response = await fetch(`${API_BASE_URL}/api/exchanges`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        setExchanges(data.data);
      } else {
        throw new Error(data.error || 'Failed to fetch exchanges');
      }

      setLoading(false);

    } catch (err) {
      console.error('Error fetching exchanges:', err);
      setError('Failed to fetch exchanges data');
      setExchanges([]);
      setLoading(false);
    }
  };

  const fetchUserExchangeSettings = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/exchanges/user-settings/${userId}`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        // Convert array to object with exchange_id as key
        const settings = {};
        data.data.forEach(setting => {
          settings[setting.exchange_id] = {
            spotEnabled: setting.spot_enabled || false,
            futuresEnabled: setting.futures_enabled || false
          };
        });
        setUserExchangeSettings(settings);
      }
    } catch (err) {
      console.error('Error fetching user exchange settings:', err);
      // Don't show error to user - just use empty settings
      setUserExchangeSettings({});
    }
  };

  const handleCheckboxChange = (exchangeId, field, value) => {
    setUserExchangeSettings(prev => ({
      ...prev,
      [exchangeId]: {
        ...prev[exchangeId],
        [field]: value
      }
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      // Prepare data for saving
      const settingsToSave = exchanges.map(exchange => ({
        exchange_id: exchange.id,
        spot_enabled: userExchangeSettings[exchange.id]?.spotEnabled || false,
        futures_enabled: userExchangeSettings[exchange.id]?.futuresEnabled || false
      }));

      const response = await fetch(`${API_BASE_URL}/api/exchanges/user-settings/${userId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ settings: settingsToSave })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        alert('Settings saved successfully!');
      } else {
        throw new Error(data.error || 'Failed to save settings');
      }

      setSaving(false);

    } catch (err) {
      console.error('Error saving settings:', err);
      setError('Failed to save settings');
      setSaving(false);
      alert('Failed to save settings: ' + err.message);
    }
  };

  if (loading) {
    return (
      <div className="loading">
        Loading exchanges data...
      </div>
    );
  }


  return (
    <div className="page-content">
      {error && (
        <div className="error" style={{ marginBottom: '20px', padding: '10px', backgroundColor: '#f8d7da', color: '#721c24', borderRadius: '4px' }}>
          {error}
        </div>
      )}

      <div className="data-table exchanges-table">
        <table>
          <thead>
            <tr>
              <th data-label="Exchange Name">Exchange Name</th>
              <th data-label="Status">Status</th>
              {userId && userId !== 0 && (
                <>
                  <th data-label="Spot Enabled">Spot Enabled</th>
                  <th data-label="Futures Enabled">Futures Enabled</th>
                </>
              )}
              <th data-label="Note">Note</th>
              <th data-label="Exchange Link">Exchange Link</th>
            </tr>
          </thead>
          <tbody>
            {exchanges.map((exchange) => (
              <tr key={exchange.id}>
                <td data-label="Exchange Name" className="exchange-name-cell">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }} className="exchange-name-content">
                    {getExchangeIcon(exchange.exchange_name) && (
                      <img
                        src={getExchangeIcon(exchange.exchange_name)}
                        alt={`${exchange.exchange_name} icon`}
                        style={{ width: '20px', height: '20px', objectFit: 'contain' }}
                        onError={(e) => { e.target.style.display = 'none'; }}
                        className="exchange-icon"
                      />
                    )}
                    <span className="exchange-name-text">{exchange.exchange_name}</span>
                  </div>
                </td>
                <td data-label="Status">
                  <span className={`status-badge ${exchange.status?.toLowerCase()}`}>
                    {exchange.status}
                  </span>
                </td>
                {userId && userId !== 0 && (
                  <>
                    <td data-label="Spot Enabled" style={{ textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        checked={userExchangeSettings[exchange.id]?.spotEnabled || false}
                        onChange={(e) => handleCheckboxChange(exchange.id, 'spotEnabled', e.target.checked)}
                        style={{ cursor: 'pointer' }}
                      />
                    </td>
                    <td data-label="Futures Enabled" style={{ textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        checked={userExchangeSettings[exchange.id]?.futuresEnabled || false}
                        onChange={(e) => handleCheckboxChange(exchange.id, 'futuresEnabled', e.target.checked)}
                        style={{ cursor: 'pointer' }}
                      />
                    </td>
                  </>
                )}
                <td data-label="Note" style={{
                  maxWidth: '300px',
                  whiteSpace: 'normal',
                  wordWrap: 'break-word',
                  overflow: 'hidden'
                }}>
                  {exchange.exchange_notes || '-'}
                </td>
                <td data-label="Exchange Link">
                  {exchange.exchange_link ? (
                    <a
                      href={exchange.exchange_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: '#8b949e', textDecoration: 'none', wordBreak: 'break-all' }}
                    >
                      {exchange.exchange_link}
                    </a>
                  ) : '-'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {userId && userId !== 0 && (
        <div className="exchanges-save-button-container" style={{ marginTop: '20px' }}>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              padding: '10px 20px',
              backgroundColor: saving ? '#6c757d' : '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: saving ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: 'bold'
            }}
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      )}

      <div style={{ marginTop: '20px', color: '#8b949e', fontSize: '11px' }}>
        Total: {exchanges.length} exchanges
      </div>
    </div>
  );
};

export default Exchanges;